/**
 * QuickConnectModal
 * =================
 * Inline modal for connecting integrations directly from the Visual Flow palette.
 * - Credential-based (Twilio, Telnyx, Webhook): Shows inline form with real-time validation
 * - OAuth-based (MS365, Google, Salesforce, etc.): Opens popup window
 * 
 * Validation flow:
 *   Submit → "Validating..." → ✅ "Credentials verified" / ❌ "Invalid" / ⚠️ "Inconclusive"
 * 
 * On success: fires onConnected callback to refresh palette immediately.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  CheckCircle,
  Plug,
  ArrowSquareOut,
  CircleNotch,
  Eye,
  EyeSlash,
  WarningCircle,
  ShieldCheck,
  ShieldWarning,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../../../config';

// Validation result states
const VSTATES = {
  IDLE: 'idle',
  VALIDATING: 'validating',
  VALID: 'valid',           // Credentials confirmed good
  INVALID: 'invalid',       // Credentials confirmed bad
  INCONCLUSIVE: 'inconclusive', // Could not verify (timeout, network)
};

const QuickConnectModal = ({ integration, onClose, onConnected }) => {
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [validationState, setValidationState] = useState(VSTATES.IDLE);
  const [validationMsg, setValidationMsg] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [oauthPolling, setOauthPolling] = useState(false);
  const pollingRef = useRef(null);
  const popupRef = useRef(null);

  const isCredential = integration?.connection_type === 'credential';
  const isOAuth = integration?.connection_type === 'oauth';

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    };
  }, []);

  const handleFieldChange = useCallback((key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setError(null);
    // Reset validation state when fields change
    if (validationState !== VSTATES.IDLE) {
      setValidationState(VSTATES.IDLE);
      setValidationMsg('');
    }
  }, [validationState]);

  const togglePasswordVisibility = useCallback((key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ── Credential-based connect (with validation) ──
  const handleCredentialSubmit = useCallback(async (e, skipValidation = false) => {
    e?.preventDefault();
    setSubmitting(true);
    setError(null);
    setValidationState(skipValidation ? VSTATES.IDLE : VSTATES.VALIDATING);
    setValidationMsg(skipValidation ? '' : 'Validating credentials...');

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/unified-capabilities/quick-connect/${integration.provider_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...formData, skip_validation: skipValidation }),
        }
      );

      const data = await res.json();
      const validation = data.validation || {};

      if (res.ok && data.success) {
        // Stored successfully
        if (validation.valid === true) {
          setValidationState(VSTATES.VALID);
          setValidationMsg(validation.message || 'Credentials verified');
        } else if (validation.valid === null) {
          setValidationState(VSTATES.INCONCLUSIVE);
          setValidationMsg(validation.message || 'Could not verify, but credentials stored');
        } else {
          setValidationState(VSTATES.VALID);
          setValidationMsg('');
        }
        setSuccess(data.message);
        setTimeout(() => {
          onConnected?.(integration.provider_id, data);
        }, 1500);
      } else if (res.ok && !data.success) {
        // Validation failed — NOT stored
        if (validation.valid === false) {
          setValidationState(VSTATES.INVALID);
          setValidationMsg(validation.message || 'Invalid credentials');
        } else {
          setError(data.message || 'Connection failed');
          setValidationState(VSTATES.IDLE);
        }
      } else {
        setError(data.detail || 'Connection failed');
        setValidationState(VSTATES.IDLE);
      }
    } catch (err) {
      setError(err.message || 'Network error');
      setValidationState(VSTATES.IDLE);
    } finally {
      setSubmitting(false);
    }
  }, [formData, integration, onConnected]);

  // ── Retry after validation failure ──
  const handleRetry = useCallback(() => {
    setValidationState(VSTATES.IDLE);
    setValidationMsg('');
    setError(null);
  }, []);

  // ── Skip validation and save anyway (for inconclusive) ──
  const handleProceedAnyway = useCallback((e) => {
    handleCredentialSubmit(e, true);
  }, [handleCredentialSubmit]);

  // ── OAuth-based connect ──
  const handleOAuthConnect = useCallback(() => {
    const settingsUrl = `${window.location.origin}${integration.settings_path || '/settings/automation-integrations'}`;
    const popup = window.open(settingsUrl, 'oauth_connect', 'width=600,height=700,scrollbars=yes');
    popupRef.current = popup;
    setOauthPolling(true);

    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/unified-capabilities/connection-status/${integration.provider_id}`,
          { credentials: 'include' }
        );
        const data = await res.json();

        if (data.connected) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setOauthPolling(false);
          setSuccess(`${integration.label} connected successfully`);

          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }

          setTimeout(() => {
            onConnected?.(integration.provider_id, data);
          }, 1200);
        }
      } catch {
        // Silently retry
      }

      if (popupRef.current?.closed) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setOauthPolling(false);
      }
    }, 2000);
  }, [integration, onConnected]);

  // ── Validation Status Banner ──
  const ValidationBanner = () => {
    if (validationState === VSTATES.VALIDATING) {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-3 flex items-center gap-2.5 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20"
          data-testid="validation-validating"
        >
          <CircleNotch size={16} className="text-indigo-400 animate-spin flex-shrink-0" />
          <p className="text-xs text-indigo-300">{validationMsg}</p>
        </motion.div>
      );
    }

    if (validationState === VSTATES.VALID && !success) {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-3 flex items-center gap-2.5 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20"
          data-testid="validation-valid"
        >
          <ShieldCheck size={16} weight="fill" className="text-emerald-400 flex-shrink-0" />
          <p className="text-xs text-emerald-300">{validationMsg}</p>
        </motion.div>
      );
    }

    if (validationState === VSTATES.INVALID) {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20"
          data-testid="validation-invalid"
        >
          <div className="flex items-start gap-2.5">
            <ShieldWarning size={16} weight="fill" className="text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-red-300">{validationMsg}</p>
              <button
                type="button"
                onClick={handleRetry}
                className="mt-2 flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
                data-testid="validation-retry-button"
              >
                <ArrowCounterClockwise size={12} />
                Edit and retry
              </button>
            </div>
          </div>
        </motion.div>
      );
    }

    if (validationState === VSTATES.INCONCLUSIVE && !success) {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20"
          data-testid="validation-inconclusive"
        >
          <div className="flex items-start gap-2.5">
            <WarningCircle size={16} weight="fill" className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-amber-300">{validationMsg}</p>
            </div>
          </div>
        </motion.div>
      );
    }

    return null;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
        data-testid="quick-connect-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="w-full max-w-md bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl shadow-2xl overflow-hidden"
          data-testid="quick-connect-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-default)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-500/15">
                <Plug size={18} weight="bold" className="text-violet-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white" data-testid="quick-connect-title">
                  Connect {integration?.label}
                </h3>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">
                  {integration?.description}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={submitting}
              className="p-1.5 rounded-md hover:bg-[var(--surface-3)] text-[var(--text-muted)] hover:text-white transition-colors"
              data-testid="quick-connect-close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            {/* Success State */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center gap-3 py-6"
                data-testid="quick-connect-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                >
                  <CheckCircle size={48} weight="fill" className="text-emerald-400" />
                </motion.div>
                <p className="text-sm font-medium text-white text-center">{success}</p>
                {/* Show validation badge in success state */}
                {validationState === VSTATES.VALID && (
                  <span className="flex items-center gap-1.5 text-xs text-emerald-400">
                    <ShieldCheck size={12} weight="fill" />
                    Credentials verified
                  </span>
                )}
                {validationState === VSTATES.INCONCLUSIVE && (
                  <span className="flex items-center gap-1.5 text-xs text-amber-400">
                    <WarningCircle size={12} weight="fill" />
                    Saved without full verification
                  </span>
                )}
                <p className="text-xs text-[var(--text-muted)] text-center">
                  Now available in your workflow. Drag it onto the canvas to use.
                </p>
              </motion.div>
            )}

            {/* Error Banner (non-validation errors) */}
            {error && !success && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20"
                data-testid="quick-connect-error"
              >
                <WarningCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-300">{error}</p>
              </motion.div>
            )}

            {/* Credential Form */}
            {isCredential && !success && (
              <form onSubmit={handleCredentialSubmit} className="space-y-3" data-testid="quick-connect-form">
                {/* Validation Banner */}
                <ValidationBanner />

                {(integration.connect_fields || []).map((field) => (
                  <div key={field.key} className="space-y-1.5">
                    <label className="text-xs font-medium text-[var(--text-secondary)]">
                      {field.label}
                      {field.required && <span className="text-red-400 ml-0.5">*</span>}
                    </label>
                    <div className="relative">
                      <input
                        type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                        required={field.required}
                        disabled={submitting || validationState === VSTATES.INVALID}
                        className="w-full px-3 py-2 text-sm bg-[var(--surface-2)] border border-[var(--border-default)] rounded-lg
                          text-white placeholder:text-[var(--text-muted)]
                          focus:outline-none focus:ring-1 focus:ring-violet-500/50 focus:border-violet-500/50
                          disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        data-testid={`quick-connect-field-${field.key}`}
                      />
                      {field.type === 'password' && (
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility(field.key)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-[var(--text-muted)] hover:text-white"
                          tabIndex={-1}
                        >
                          {showPasswords[field.key] ? <EyeSlash size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Submit / Retry buttons */}
                {validationState !== VSTATES.INVALID && (
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                      bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium
                      disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="quick-connect-submit"
                  >
                    {submitting ? (
                      <>
                        <CircleNotch size={14} className="animate-spin" />
                        {validationState === VSTATES.VALIDATING ? 'Validating...' : 'Connecting...'}
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={14} weight="bold" />
                        Validate & Connect
                      </>
                    )}
                  </button>
                )}
              </form>
            )}

            {/* OAuth Connect */}
            {isOAuth && !success && (
              <div className="space-y-4" data-testid="quick-connect-oauth">
                <p className="text-xs text-[var(--text-secondary)]">
                  {integration?.label} uses secure OAuth authentication. 
                  A popup window will open for you to authorize access.
                </p>

                {oauthPolling ? (
                  <div className="flex flex-col items-center gap-3 py-4">
                    <CircleNotch size={28} className="animate-spin text-violet-400" />
                    <p className="text-sm text-[var(--text-secondary)]">
                      Waiting for authorization...
                    </p>
                    <p className="text-xs text-[var(--text-muted)]">
                      Complete the sign-in in the popup window
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={handleOAuthConnect}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                      bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors"
                    data-testid="quick-connect-oauth-button"
                  >
                    <ArrowSquareOut size={14} weight="bold" />
                    Connect with {integration?.label}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {!success && (
            <div className="px-5 py-3 border-t border-[var(--border-default)] bg-[var(--surface-2)]/30">
              <p className="text-[10px] text-[var(--text-muted)] text-center">
                Credentials are encrypted at rest and scoped to your organization.
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuickConnectModal;
